import React, { useState, useRef, useEffect, useContext } from 'react';
import { Wizard, StepStatus } from '@business-app/fabric/lib/components/Wizard';

import { Success } from 'components/deployment/steps/Success';
import { scrollToTop } from 'utils/browser';
import { Announce } from '../common/Announce';
import { ApiError } from 'services/restService';
import { WizardTitle } from '../common/WizardTitle';
import { AzureConfiguration } from './steps/AzureConfiguration';
import { startDeployment } from 'services/startDeploymentService';
import { Step, StepBaseProps, BaseState, StepRef, ProcessInfo, ConfigurationInfo, style, wizardStyle, progressStyle, footerStyle } from 'components/common/Wizard';
import { Configuration } from './steps/Configuration';
import translationsProvider from 'translations/translationsProvider';
import TranslationsContext from 'translations/TranslationsContext';
import translationKeys from 'translationKeys';


export interface StepProps extends StepBaseProps {
	deployment?: IDeployment,
}

export type StepComponent = (props: StepProps) => JSX.Element;

export interface IDeployment extends ProcessInfo, ConfigurationInfo { }

export interface IState extends BaseState {
	deployment?: {
		id: string,
		url: string,
		organizationId: string,
		subscriptionId: string,
		resourceGroupId: string,
	}
}

const initialSteps: Step[] = [
	{
		name: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.One.Name),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.One.AriaDescription)
	},
	{
		name: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.Two.Name),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.Two.AriaDescription)
	},
	{
		name: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.Three.Name),
		summary: [],
		hasError: false,
		ariaDescription: translationsProvider.staticTranslate(translationKeys.Deployment.InitialSteps.Three.AriaDescription)
	}
];

const stepsComponentList: StepComponent[] = [
	(props) => <Configuration isTerms={true} {...props} />,
	(props) => <AzureConfiguration {...props} />,
	(props) => <Success {...props} />
];

const initialState: IState = {
	steps: initialSteps,
	stepIndex: 0,
	error: undefined,
	canGoNext: false,
	accNotification: undefined,
	deployment: {
		id: undefined,
		url: undefined,
		organizationId: undefined,
		subscriptionId: undefined,
		resourceGroupId: undefined,
	}
};

export const Deployment = () => {
	const { t } = useContext(TranslationsContext)!;
	const [state, setState] = useState<IState>({ ...initialState });

	const { steps, stepIndex, canGoNext, deployment, error, accNotification } = state;
	const isLastStep = stepIndex === steps.length - 1;

	useEffect(scrollToTop, [stepIndex, error]);

	const stepRef = useRef<StepRef>({
		validateStep: () => Promise.resolve(true),
		getDatafromStep: () => Promise.resolve({}),
	});

	const setStepError = (index: number, error?: ApiError) => {
		const stepsToAddError = [...steps];
		stepsToAddError[index].hasError = true;

		setState({ ...state, error, steps: stepsToAddError, accNotification: getStepErrorNotification(index) });
	};

	const resetErrors = () => {
		const newSteps = steps.map((step) => {
			step.hasError = false;

			return step;
		});

		setState({ ...state, steps: newSteps, error: null });
	};

	const restartDeployment = () => {
		setState({ ...initialState });
	};

	const resetStepRef = () => {
		stepRef.current.validateStep = () => Promise.resolve(true);
		stepRef.current.getDatafromStep = () => Promise.resolve({});
	};

	const getStatuses = () => steps.map((s, index, list) => {
		if (s.hasError) {
			return StepStatus.Error;
		}

		if (stepIndex === list.length - 1) {
			return StepStatus.Valid;
		}

		if (index >= stepIndex) {
			return StepStatus.Empty;
		}

		return StepStatus.Valid;
	});

	const getStepInitiator = (index: number): (deployment: IDeployment) => Promise<IState> => {
		const defaultInitiator = (deployment: IDeployment) => Promise.resolve({ ...state });

		return [callConfigurationStepApi][index] || defaultInitiator;
	};

	const getStepRenderer = (): StepComponent => {
		const step = stepsComponentList[stepIndex];
		const fallbackStep = (props: StepProps) => <h2>{t(translationKeys.Deployment.Fallback)}</h2>;

		return step || fallbackStep;
	};

	const checkCanGoNext = (newCanGo: boolean) => {
		if (newCanGo !== canGoNext) {
			setState({ ...state, canGoNext: newCanGo });
		}
	};

	const callConfigurationStepApi = async ({ organizationId, subscriptionId, resourceGroupId }: IDeployment): Promise<IState> => {
		try {
			const res = await startDeployment(organizationId, subscriptionId, resourceGroupId);
			return Promise.resolve({
				...state,
				deployment: {
					...deployment,
					organizationId: organizationId,
					subscriptionId: subscriptionId,
					resourceGroupId: resourceGroupId,
					id: res.deploymentId,
					url: res.nextUrl
				}
			});
		} catch (e) {
			return Promise.reject(e);
		}
	};

	const goToStep = async (index: number) => {
		const isForward = index > stepIndex;
		const nextIndex = stepIndex + 1;
		resetErrors();

		if (isForward) {
			if (await stepRef.current.validateStep()) {
				const stepData = await stepRef.current.getDatafromStep();
				const newDeployment = { ...deployment, ...stepData };

				const stepInitiator = getStepInitiator(stepIndex);
				stepInitiator(newDeployment)
					.then((state: IState) => {
						resetStepRef();
						setState({
							...state,
							stepIndex: nextIndex,
							accNotification: getStepChangeNotification(nextIndex),
						});
					}).catch((error) => {
						setState({
							...state,
							error,
							accNotification: getStepErrorNotification(index)
						});
						setStepError(stepIndex);
					});
			}
		} else if (index === 0) {
			restartDeployment();
		}
	};

	const goToNextStep = () => goToStep(stepIndex + 1);

	// Accessibility related functionality 

	const getStepName = (stepIndex: number): string => initialSteps[stepIndex]?.name;

	const getStepChangeNotification = (index: number) => t(translationKeys.Deployment.Notifications.StepChanged, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	const getStepErrorNotification = (index: number) => t(translationKeys.Deployment.Notifications.StepError, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	const getMainAriaLabel = (index: number) => t(translationKeys.Deployment.Labels.MainArea, { stepName: getStepName(index), stepNumber: index + 1, totalStepsCount: initialSteps.length });

	// End of Accessibility related functionality

	const onRenderStep = () => {
		const stepRenderer = getStepRenderer();

		return (
			<main className={style.stepShell} aria-label={getMainAriaLabel(stepIndex)}>
				{
					stepRenderer({
						error,
						stepRef,
						deployment,
						goToNextStep,
						resetErrors,
						checkCanGoNext,
						setStepError: (error) => setStepError(stepIndex, error)
					})
				}
			</main>
		);
	};

	return (
		<>
			<Announce message={accNotification} />
			<WizardTitle content={t(translationKeys.Deployment.WizardTitle.Content)} />
			<Wizard
				styles={wizardStyle}
				progressProps={{
					currentIndex: stepIndex,
					stepNames: steps.map(s => s.name),
					stepStatuses: getStatuses(),
					stepSummaries: steps.map(s => s.summary),
					computeAriaDescription: index => steps[index].ariaDescription,
					navLabel: t(translationKeys.Deployment.Wizard.NavLabel),
					expandButtonLabel: t(translationKeys.Deployment.Wizard.ExpandButtonLabel),
					collapseButtonLabel: t(translationKeys.Deployment.Wizard.CollapseButtonLabel),
					styles: progressStyle
				}}
				footerProps={{
					styles: footerStyle,
					backButtonText: t(translationKeys.Deployment.Wizard.Footer.Back),
					canGoToNextStep: (canGoNext && stepIndex < steps.length),
					canGoToPreviousStep: !!error,
					continueButtonText: t(translationKeys.Deployment.Wizard.Footer.Continue),
					shouldDisplayBackButton: stepIndex !== 0,
					goToPreviousStep: () => goToStep(0),
					goToNextStep,
					renderFooterContent: (buttonRender, summaryRender) => !isLastStep && <section aria-label={t(translationKeys.Deployment.Wizard.Footer.Content.AriaLabel)}>{buttonRender()}</section>
				}}
				onRenderStep={onRenderStep}
			/>
		</>
	);
};
